import { PercentageUnits } from "@/libs/units/percentage";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import {
  MAX_PUMP_OUTSIDE_RANGE_RATIO,
  MIN_CLEARANCE
} from "../validatorConstants";
import { getOutsideROR } from "@/libs/calculations/pumps/pumpData";
import {
  EquipmentCalculatorSectionKey,
  type ScenarioCalculations
} from "@/composables/scenarioCalculations/calculationTypes";
import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { Maybe } from "graphql/jsutils/Maybe";

// The pump_outside_graphing_range rule as plain functions, shared with the
// predictive flow-rate solver. The solver needs only this rule, and getting
// it from the validator's errors meant evaluating every rule for every
// candidate flow rate. No rule lists it in disablesRules, so "active and
// invalid" is the same as "present in the validator's errors".
const isPumpGraphingRangeCheckActive = (
  calcs: ScenarioCalculations,
  parameters: Maybe<OperatingParameters>
): boolean =>
  calcs.ascendingOrderPumps.value.length > 0 &&
  valuesExist([parameters?.target_liquid_rate_std_barrel_day]);

const getPumpsExceedingGraphMax = (calcs: ScenarioCalculations) =>
  calcs.ascendingOrderPumps.value.filter(
    xref =>
      calcs.pumpCalculations.individual.value[xref.id]
        .pumpIntakeFlowRateExceedsGraphMax
  );

export const isPumpOutsideGraphingRange = (
  calcs: ScenarioCalculations,
  parameters: Maybe<OperatingParameters>
): boolean =>
  isPumpGraphingRangeCheckActive(calcs, parameters) &&
  getPumpsExceedingGraphMax(calcs).length > 0;

export const pumpChecksNames = [
  "pump_size_top_casing",
  "pump_size_bottom_casing",
  "pump_size_bypass_tubing_top_casing",
  "pump_size_bypass_tubing_bottom_casing",
  "hal_pump_diameter",
  "tubing_length_exceeds_last_survey_depth",
  "gas_lock",
  "pump_outside_graphing_range",
  "pump_outside_operating_range",
  "pump_rate_correction_ratio_below_threshold",
  "pump_head_correction_ratio_below_threshold",
  "pump_bhp_correction_ratio_below_threshold",
  "motor_temperature_data_accuracy",
  "bolt_loading_exceeded",
  "shut_in_bolt_loading_exceeded",
  "shut_in_burst_limit_hs",
  "shut_in_burst_limit_std",
  "operating_burst_limit_hs",
  "operating_burst_limit_std",
  "pump_shaft_limit_hs",
  "pump_shaft_limit_std",
  "legacy_empty_housings",
  "pump_rotation_mismatch"
] as const;

export const pumpChecks: ValidationRuleGenerator = ({
  parameters,
  wellProperties,
  calcs,
  equipment,
  stack
}) => {
  return [
    // Size check
    {
      name: "pump_size_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Pump too large for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) && calcs.ascendingOrderPumps.value.length > 0,
      isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            calcs.pumpCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              calcs.pumpCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_size_bypass_tubing_top_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Pump too large with bypass tubing for top casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_top_casing?.drift_inches,
          wellProperties.value?.top_casing_length_feet
        ]) &&
        calcs.ascendingOrderPumps.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
        return (
          wellProperties.value?.r_top_casing?.drift_inches -
            (calcs.pumpCalculations.individual.value[xref.id].xrefPump.r_pump
              ?.housing_outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => {
          return (
            wellProperties.value?.r_top_casing?.drift_inches -
              (calcs.pumpCalculations.individual.value[xref.id].xrefPump.r_pump
                ?.housing_outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_size_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Pump too large for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfPumpsDepthFt.value >
          wellProperties.value?.top_casing_length_feet,
      isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            calcs.pumpCalculations.individual.value[xref.id]
              .maxCalculatedOuterDiameterIn <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              calcs.pumpCalculations.individual.value[xref.id]
                .maxCalculatedOuterDiameterIn <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_size_bypass_tubing_bottom_casing",
      message: addStackPrefixToMessage(
        `Clearance Error: Pump too large with bypass tubing for bottom casing`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive:
        valuesExist([
          wellProperties.value?.r_bottom_casing?.drift_inches,
          wellProperties.value?.bottom_casing_length_feet
        ]) &&
        calcs.equipmentLengths.bottomOfPumpsDepthFt.value >
          wellProperties.value?.top_casing_length_feet &&
        calcs.ascendingOrderPumps.value.length > 0 &&
        !!equipment.value?.use_uphole_y_tool &&
        calcs.orderedYToolBypassTubings.value.length > 0,
      isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
        return (
          wellProperties.value?.r_bottom_casing?.drift_inches -
            (calcs.pumpCalculations.individual.value[xref.id].xrefPump.r_pump
              ?.housing_outer_diameter_inches +
              calcs.yToolBypassTubingMaxODIn.value) <
          MIN_CLEARANCE
        );
      }),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => {
          return (
            wellProperties.value?.r_bottom_casing?.drift_inches -
              (calcs.pumpCalculations.individual.value[xref.id].xrefPump.r_pump
                ?.housing_outer_diameter_inches +
                calcs.yToolBypassTubingMaxODIn.value) <
            MIN_CLEARANCE
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "hal_pump_diameter",
      message: addStackPrefixToMessage(
        `Selected Pump Requires HAL Intake`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          xref.r_pump?.manufacturer === "HAL" &&
          xref.r_pump.housing_outer_diameter_inches === 4 &&
          calcs.orderedIntakes.value.every(
            xref => xref.r_intake?.manufacturer !== "HAL"
          )
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            xref.r_pump?.manufacturer === "HAL" &&
            xref.r_pump.housing_outer_diameter_inches === 4 &&
            calcs.orderedIntakes.value.every(
              xref => xref.r_intake?.manufacturer !== "HAL"
            )
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },

    {
      name: "tubing_length_exceeds_last_survey_depth",
      message:
        "Tubing length exceeds the last measured depth of the deviation survey",
      severity: Severity.warn,
      isActive: calcs.hasDeviationSurvey.value,
      isInvalid: (() => {
        const lastSurveyDepth =
          calcs.entryRows.value.data.slice(-1)[0]?.[0] || 0;
        return calcs.equipmentLengths.tubingLengthFt.value > lastSurveyDepth;
      })(),
      rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
    },

    // Gas locking check
    {
      name: "gas_lock",
      message: addStackPrefixToMessage(
        `Gas into pump is beyond the gas locking threshold`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id].gasAtIntakeRatio /
            calcs.pumpCalculations.individual.value[xref.id].gasLockRatio >=
          1
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id].gasAtIntakeRatio /
              calcs.pumpCalculations.individual.value[xref.id].gasLockRatio >=
            1
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },

    // ROR Check
    {
      name: "pump_outside_graphing_range",
      message: addStackPrefixToMessage(
        `You are using this pump outside of its max operating range. Pump is being limited to graphMax flow rate for BHP`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: isPumpGraphingRangeCheckActive(calcs, parameters.value),
      isInvalid: getPumpsExceedingGraphMax(calcs).length > 0,
      affectedXrefIds: getPumpsExceedingGraphMax(calcs).map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_outside_operating_range",
      message: addStackPrefixToMessage(
        `You are using this pump over ${PercentageUnits.Ratio.toStandardUnit(
          MAX_PUMP_OUTSIDE_RANGE_RATIO
        )}% outside of its operating range`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
        const pumpCalcs = calcs.pumpCalculations.individual.value[xref.id];

        const effectiveIntakeFlowRate =
          pumpCalcs.intakeStageCalculations?.intakeFluidPropertiesContext
            .fluidProperties.fluidFlowRateBBLD;
        const effectiveDischargeFlowRate =
          pumpCalcs.dischargeStageCalculations?.dischargeFluidPropertiesContext
            .fluidProperties.fluidFlowRateBBLD;

        const outsideROR = getOutsideROR(
          pumpCalcs.rorMin,
          pumpCalcs.rorMax,
          effectiveIntakeFlowRate,
          effectiveDischargeFlowRate
        );

        if (xref.housings_order) {
          return (
            xref.housings_order.length > 0 &&
            outsideROR > MAX_PUMP_OUTSIDE_RANGE_RATIO
          );
        } else {
          return false;
        }
      }),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => {
          const pumpCalcs = calcs.pumpCalculations.individual.value[xref.id];

          const outsideROR = getOutsideROR(
            pumpCalcs.rorMin,
            pumpCalcs.rorMax,
            pumpCalcs.intakeStageCalculations?.intakeFluidPropertiesContext
              .fluidProperties.fluidFlowRateBBLD,
            pumpCalcs.dischargeStageCalculations
              ?.dischargeFluidPropertiesContext.fluidProperties
              .fluidFlowRateBBLD
          );
          return (
            xref.housings_order &&
            xref.housings_order.length > 0 &&
            outsideROR > MAX_PUMP_OUTSIDE_RANGE_RATIO
          );
        })
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    // Rate correction ratio
    {
      name: "pump_rate_correction_ratio_below_threshold",
      message: addStackPrefixToMessage(
        `Viscosity Correction too Low. Please Review`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref => xref.rate_correction_ratio < 0.5
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => xref.rate_correction_ratio < 0.5)
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    // Head correction ratio
    {
      name: "pump_head_correction_ratio_below_threshold",
      message: addStackPrefixToMessage(
        `Head Correction too Low. Please Review`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref => xref.head_correction_ratio < 0.5
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => xref.head_correction_ratio < 0.5)
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    // BHP correction ratio
    {
      name: "pump_bhp_correction_ratio_below_threshold",
      message: addStackPrefixToMessage(
        `BHP Correction too Low. Please Review`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref => xref.brake_horsepower_correction_ratio < 0.5
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(xref => xref.brake_horsepower_correction_ratio < 0.5)
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    // non-Summit motor temperature data accuracy
    // Remove for now. Could be added back later
    // {
    //   name: "motor_temperature_data_accuracy",
    //   message:
    //     "Motor temperatures may not be accurate for non-Summit motors. Contact the manufacturer for accurate motor temperature data.",
    //   severity: Severity.warn,
    //   isActive: !!equipment.value?.motor_id,
    //   isInvalid: !equipment.value?.r_motor?.manufacturer
    //     ?.toLowerCase()
    //     .includes("summit"),
    //   rulePlacement: EquipmentCalculatorSectionKey.MOTOR
    // },
    {
      name: "bolt_loading_exceeded",
      message:
        "Bolt Loading Exceeded - Potential for pump parting during operation",
      severity: Severity.error,
      isActive: calcs.ascendingOrderPumps.value.length > 0,
      isInvalid:
        calcs.pumpCalculations.aggregate.axialBoltLoadRatio >
        calcs.primaryPump.value?.r_pump?.max_bolt_load_pounds,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "shut_in_bolt_loading_exceeded",
      message:
        "Bolt Loading Exceeded - Potential for pump parting during operation while shut-in",
      severity: Severity.error,
      isActive: calcs.ascendingOrderPumps.value.length > 0,
      isInvalid:
        calcs.pumpCalculations.aggregate.axialBoltLoadShutInRatio >
        calcs.primaryPump.value?.r_pump?.max_bolt_load_pounds,
      rulePlacement: EquipmentCalculatorSectionKey.PUMP // This rule is not equipment specific
    },

    // Shut-in Burst Limit
    {
      name: "shut_in_burst_limit_hs",
      message: addStackPrefixToMessage(
        `Shut-in MAWP exceeded for HS`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id]
            .shutInBurstRatioHighStrength > 1
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id]
              .shutInBurstRatioHighStrength > 1
        )
        .map(xref => xref.id),
      disablesRules: ["shut_in_burst_limit_std"],
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "shut_in_burst_limit_std",
      message: addStackPrefixToMessage(
        `Shut-in MAWP exceeded for STD, use high-strength material`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id]
            .shutInBurstRatioStandard > 1
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id]
              .shutInBurstRatioStandard > 1
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },

    // Operating Burst Limit
    {
      name: "operating_burst_limit_hs",
      message: addStackPrefixToMessage(
        `Operating burst limit exceeded for HS`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id]
            .operatingBurstRatioHighStrength > 1
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id]
              .operatingBurstRatioHighStrength > 1
        )
        .map(xref => xref.id),
      disablesRules: ["operating_burst_limit_std"],
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "operating_burst_limit_std",
      message: addStackPrefixToMessage(
        `Operating burst limit exceeded for STD, use high-strength material`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id]
            .operatingBurstRatioStandard > 1
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id]
              .operatingBurstRatioStandard > 1
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },

    // Shaft Limit
    {
      name: "pump_shaft_limit_hs",
      message: addStackPrefixToMessage(
        `HP greater than Pump Shaft Limit (HS)`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          calcs.pumpCalculations.individual.value[xref.id]
            .shaftPowerLimitHighStrengthHP <
          calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            calcs.pumpCalculations.individual.value[xref.id]
              .shaftPowerLimitHighStrengthHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      disablesRules: ["pump_shaft_limit_std"],
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_shaft_limit_std",
      message: addStackPrefixToMessage(
        `HP greater than Pump Shaft Limit (STD), high-strength pump shaft required`,
        stack,
        equipment
      ),
      severity: Severity.warn,
      isActive: true,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          !xref.use_high_strength_shaft &&
          calcs.pumpCalculations.individual.value[xref.id]
            .shaftPowerLimitStandardHP <
            calcs.motorCalculations.totalRequiredBrakeHP.value
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            !xref.use_high_strength_shaft &&
            calcs.pumpCalculations.individual.value[xref.id]
              .shaftPowerLimitStandardHP <
              calcs.motorCalculations.totalRequiredBrakeHP.value
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },

    // Legacy imported sizing has no longer valid empty housings
    {
      name: "legacy_empty_housings",
      message: "Remove and re-select pump to fix empty housings",
      severity: Severity.error,
      isActive: calcs.ascendingOrderPumps.value.length > 0,
      isInvalid: calcs.ascendingOrderPumps.value.some(
        xref =>
          (xref.total_stages || 0) > 0 &&
          (xref.r_pump?.r_housings.length || 0) > 0 &&
          calcs.orderedHousings.value.filter(
            xrefHousing =>
              xrefHousing.scenario_equipment_xref_pump_id === xref.id
          ).length === 0
      ),
      affectedXrefIds: calcs.ascendingOrderPumps.value
        .filter(
          xref =>
            (xref.total_stages || 0) > 0 &&
            (xref.r_pump?.r_housings.length || 0) > 0 &&
            calcs.orderedHousings.value.filter(
              xrefHousing =>
                xrefHousing.scenario_equipment_xref_pump_id === xref.id
            ).length === 0
        )
        .map(xref => xref.id),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    },
    {
      name: "pump_rotation_mismatch",
      message: addStackPrefixToMessage(
        `Taper Pump Rotation does not match Primary Pump. Please select another Stage Type`,
        stack,
        equipment
      ),
      severity: Severity.error,
      isActive: calcs.descendingOrderPumps.value.length > 1,
      isInvalid: calcs.descendingOrderPumps.value
        .slice(1)
        .some(
          xref =>
            xref.r_pump?.rotation !== calcs.primaryPump.value?.r_pump?.rotation
        ),
      affectedXrefIds: calcs.descendingOrderPumps.value
        .slice(1)
        .filter(
          xref =>
            xref.r_pump?.rotation !== calcs.primaryPump.value?.r_pump?.rotation
        )
        .map(xref => {
          return xref.id;
        }),
      rulePlacement: EquipmentCalculatorSectionKey.PUMP
    }
  ];
};
